import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../Components/Button/Button';
import PublicHeader from '../../Layouts/PublicHeader/PublicHeader';

const Landing = () => {
  const navigate = useNavigate();
  const heroContentRef = useRef(null);
  const featuresRef = useRef(null);
  const stepsRef = useRef(null);
  const ctaRef = useRef(null);
  const pricingRef = useRef(null);

  useEffect(() => {
    // Enable body scroll on landing page
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.getElementById('root').style.overflow = 'auto';
    document.querySelector('.App').style.overflow = 'auto';

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const elements = [
      heroContentRef.current,
      ...(featuresRef.current?.children || []),
      ...(stepsRef.current?.children || []),
      ctaRef.current,
      ...(pricingRef.current?.children || [])
    ].filter(Boolean);

    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
      // Restore original overflow settings when leaving landing page
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      const root = document.getElementById('root');
      if (root) root.style.overflow = '';
      const app = document.querySelector('.App');
      if (app) app.style.overflow = '';
    };
  }, []);

  return (
    <div className="landing-page">
      <PublicHeader />

      <section 
        className="landing-hero landing-bg-image"
        style={{
          backgroundImage: `url(${window.getPublicUrl('/assets/images/LandingPageBackgroungTopImage.png')})`
        }}
      >
        <div className="landing-hero-overlay"></div>
        <div className="landing-hero-content" ref={heroContentRef}>
          <span className="landing-hero-badge animate-fade-in">Automated Tenant Screening</span>
          <h1 className="landing-hero-title animate-fade-in-delay-1">Simplifying tenant Screening for landlords.</h1>
          <p className="landing-hero-subtitle animate-fade-in-delay-2">
            Let our automated system handle tenant screenings, Document verification, and reference checks.
          </p>
          <div className="landing-hero-buttons animate-fade-in-delay-3">
            <Button variant="outline" className="landing-hero-button">
              Watch Demo
            </Button>
            <Button 
              variant="success" 
              className="landing-hero-button"
              onClick={(e) => {
                e.preventDefault();
                navigate('/choose-role');
              }}
            >
              Get started
            </Button>
          </div>
        </div>
      </section>

      <section className="landing-features" id="features" ref={featuresRef}>
        <div className="landing-feature-card">
          <div className="landing-feature-icon">
            <img src={window.getPublicUrl("/assets/images/automated.png")} alt="Automated Pre-Screening" />
          </div>
          <h3 className="landing-feature-title">Automated Pre-Screening</h3>
          <p className="landing-feature-description">Screen tenants with our smart system.</p>
        </div>
        <div className="landing-feature-card">
          <div className="landing-feature-icon">
            <img src={window.getPublicUrl("/assets/images/secure.png")} alt="Secure Background Checks" />
          </div>
          <h3 className="landing-feature-title">Secure Background Checks</h3>
          <p className="landing-feature-description">Fast, reliable responses from trusted partners.</p>
        </div>
        <div className="landing-feature-card">
          <div className="landing-feature-icon">
            <img src={window.getPublicUrl("/assets/images/save.png")} alt="Save Time & Reduce Vacancy" />
          </div>
          <h3 className="landing-feature-title">Save Time & Reduce Vacancy</h3>
          <p className="landing-feature-description">Fill units faster with qualified renters.</p>
        </div>
      </section>

      <section className="landing-how-it-works" id="how-it-works">
        <h2 className="landing-section-title">How Clear Rent Works</h2>
        <div className="landing-steps" ref={stepsRef}>
          <div className="landing-step-card">
            <div className="landing-step-number">1</div>
            <h3 className="landing-step-title">Quick Pre Screening</h3>
            <p className="landing-step-description">
              Tenants call or apply online. We ask the key questions for you.
            </p>
          </div>
          <div className="landing-step-card">
            <div className="landing-step-number">2</div>
            <h3 className="landing-step-title">Easy Background Checks</h3>
            <p className="landing-step-description">
              Landlords request, tenants complete securely.
            </p>
          </div>
        </div>
      </section>

      <section 
        className="landing-cta-section landing-bg-image"
        style={{
          backgroundImage: `url(${window.getPublicUrl('/assets/images/landingPageBackground.png')})`
        }}
      >
        <div className="landing-cta-overlay"></div>
        <div className="landing-cta-content" ref={ctaRef}>
          <h2 className="landing-cta-title">Ready to Screen Smarter?</h2>
          <p className="landing-cta-subtitle">
            Join thousands of landlords who trust AI to find quality tenants.
          </p>
          <Button 
            variant="success" 
            size="large" 
            className="landing-cta-button-main"
            onClick={(e) => {
              e.preventDefault();
              navigate('/choose-role');
            }}
          >
            Get Started Now
          </Button>
        </div>
      </section>

      <section className="landing-pricing" id="pricing">
        <h2 className="landing-section-title">Simple, Transparent Pricing</h2>
        <p className="landing-section-subtitle">
          One plan for landlords. Pay-per-screening for tenants.
        </p>
        <div className="landing-pricing-cards" ref={pricingRef}>
          <div className="landing-pricing-card">
            <span className="landing-pricing-label">For Landlord</span>
            <div className="landing-pricing-amount">
              <span className="pricing-amount-number">$11.99</span>
              <span className="pricing-amount-unit">/month</span>
            </div>
            <p className="landing-pricing-description">Full access to the platform.</p>
            <ul className="landing-pricing-features">
              <li>
                <img src={window.getPublicUrl("/assets/images/CircleCheckIcon.svg")} alt="Check" className="pricing-check-icon" />
                Unlimited property listings
              </li>
              <li>
                <img src={window.getPublicUrl("/assets/images/CircleCheckIcon.svg")} alt="Check" className="pricing-check-icon" />
                Full tenant screening reports
              </li>
              <li>
                <img src={window.getPublicUrl("/assets/images/CircleCheckIcon.svg")} alt="Check" className="pricing-check-icon" />
                Automated verification results
              </li>
              <li>
                <img src={window.getPublicUrl("/assets/images/CircleCheckIcon.svg")} alt="Check" className="pricing-check-icon" />
                Reference check summaries
              </li>
              <li>
                <img src={window.getPublicUrl("/assets/images/CircleCheckIcon.svg")} alt="Check" className="pricing-check-icon" />
                Document authenticity analysis
              </li>
            </ul>
          </div>
          <div className="landing-pricing-card">
            <span className="landing-pricing-label">For Tenant</span>
            <div className="landing-pricing-amount">
              <span className="pricing-amount-number">$9.99</span>
              <span className="pricing-amount-unit">/screening</span>
            </div>
            <p className="landing-pricing-description">One-time fee per Landlord.</p>
            <ul className="landing-pricing-features">
              <li>
                <img src={window.getPublicUrl("/assets/images/CircleCheckIcon.svg")} alt="Check" className="pricing-check-icon" />
                Automated voice interview
              </li>
              <li>
                <img src={window.getPublicUrl("/assets/images/CircleCheckIcon.svg")} alt="Check" className="pricing-check-icon" />
                Document upload & verification
              </li>
              <li>
                <img src={window.getPublicUrl("/assets/images/CircleCheckIcon.svg")} alt="Check" className="pricing-check-icon" />
                Reference check coordination
              </li>
              <li>
                <img src={window.getPublicUrl("/assets/images/CircleCheckIcon.svg")} alt="Check" className="pricing-check-icon" />
                Secure data handling
              </li>
              <li>
                <img src={window.getPublicUrl("/assets/images/CircleCheckIcon.svg")} alt="Check" className="pricing-check-icon" />
                Results shared with landlord
              </li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          {/* <div className="landing-footer-left">
            <Link to="/" className="landing-footer-logo-link">
              <img
                src={window.getPublicUrl('/assets/images/Logo.png')}
                alt="ClearRent"
                className="landing-footer-logo"
              />
            </Link>
          </div> */}
          <div className="landing-footer-center">
            <span className="landing-footer-text">
              © {new Date().getFullYear()} ClearRent. All rights reserved.
            </span>
          </div>
          <nav className="landing-footer-links">
            <Link to="/terms" className="landing-footer-link">
              Terms &amp; Privacy
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
